import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon, Clear as ClearIcon } from "@mui/icons-material";
import MDTypography from "../../../../components/MDTypography";
import { sheetsSettingsApi } from "../../../../services/api/jtl/sheets";

const SheetSettingsSubTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  //  sheet settings
  const [SheetSettings, setSheetSettings] = useState({
    spreadsheetId: "",
    sheetId: "",
    version: "v4",
  });

  // URL state
  const [SheetsUrl, setSheetsUrl] = useState("");
  const [SheetsUrlError, setSheetsUrlError] = useState("");

  //  column settings
  const [ColumnSettings, setColumnSettings] = useState({
    dateColumn: "",
    orderPlaceColumn: "",
    unitColumn: "",
    asinSkuColumn: "",
    sendStatusColumn: "",
    confirmationColumn: "",
  });

  useEffect(() => {
    // Load initial data with error handling
    const initializeData = async () => {
      try {
        // Load all data in parallel but handle each error separately
        const loadOperations = [
          loadSheetsSettings().catch((error) => {
            console.error("Failed to load sheets settings on init:", error);
            return null; // Continue with other operations
          }),
        ];

        await Promise.allSettled(loadOperations);
      } catch (error) {
        console.error("Unexpected error during initialization:", error);
        setError(getErrorMessage(error));
      }
    };

    initializeData();
  }, []);

  // Helper functions
  const handleSheetSettingsChange = (field, value) => {
    setSheetSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSheetsUrlChange = (url) => {
    setSheetsUrl(url);
    if (url.trim()) {
      try {
        const result = extractIdsFromSheetsUrl(url);

        if (result.success) {
          setSheetSettings((prev) => ({
            ...prev,
            spreadsheetId: result.spreadsheetId,
            sheetId: result.sheetId,
          }));
        } else {
          setSheetsUrlError(
            "Could not extract Spreadsheet ID and Sheet ID from URL",
          );
        }
      } catch (error) {
        setSheetsUrlError("Invalid Google Sheets URL format");
      }
    } else {
      setSheetsUrlError("");
    }
  };

  const handleColumnSettingsChange = (field, value) => {
    setColumnSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // API functions
  const loadSheetsSettings = async (forceRefresh = false) => {
    try {
      console.log("Loading  sheets settings...");

      const CACHE_KEY = "jtl_sheet_settings_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      let settings;

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              settings = data;
              console.log("Loaded sheets settings from cache");
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      if (!settings) {
        const response = await sheetsSettingsApi.getSettings();
        settings = response.data;
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data: settings,
            timestamp: Date.now(),
          }),
        );
      }

      if (settings) {
        setSheetSettings({
          spreadsheetId: settings.spreadsheet_id || "",
          sheetId: settings.sheet_id || "",
          version: settings.version || "v4",
        });

        // Construct URL from loaded settings
        if (settings.spreadsheet_id) {
          const url = `https://docs.google.com/spreadsheets/d/${
            settings.spreadsheet_id
          }/edit${settings.sheet_id ? `?gid=${settings.sheet_id}` : ""}`;
          setSheetsUrl(url);
        }

        setColumnSettings({
          dateColumn: settings.date_column || "",
          orderPlaceColumn: settings.order_place_column || "",
          unitColumn: settings.unit_column || "",
          asinSkuColumn: settings.asin_sku_column || "",
          sendStatusColumn: settings.send_status_column || "",
          confirmationColumn: settings.confirmation_column || "",
        });
      }

      console.log(" sheets settings loaded successfully");
    } catch (error) {
      console.error("Error loading  sheets settings:", error);
      throw error;
    }
  };

  const saveSheetsSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log("Saving  sheets settings...");
      localStorage.removeItem("jtl_sheet_settings_cache");

      const settingsData = {
        spreadsheet_id: SheetSettings.spreadsheetId,
        sheet_id: SheetSettings.sheetId,
        version: SheetSettings.version,
        date_column: ColumnSettings.dateColumn,
        order_place_column: ColumnSettings.orderPlaceColumn,
        unit_column: ColumnSettings.unitColumn,
        asin_sku_column: ColumnSettings.asinSkuColumn,
        send_status_column: ColumnSettings.sendStatusColumn,
        confirmation_column: ColumnSettings.confirmationColumn,
      };

      await sheetsSettingsApi.updateSettings(settingsData, "");

      setSuccess(" sheets settings saved successfully!");
      console.log(" sheets settings saved successfully");
    } catch (error) {
      console.error("Error saving  sheets settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearSheetsSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log("Clearing  sheets settings...");
      localStorage.removeItem("jtl_sheet_settings_cache");

      await sheetsSettingsApi.resetSettings();

      // Reset all state
      setSheetSettings({
        spreadsheetId: "",
        sheetId: "",
        version: "v4",
      });
      setSheetsUrl("");
      setSheetsUrlError("");
      setColumnSettings({
        dateColumn: "",
        orderPlaceColumn: "",
        unitColumn: "",
        asinSkuColumn: "",
        sendStatusColumn: "",
        confirmationColumn: "",
      });

      setSuccess(" sheets settings cleared successfully!");
      console.log(" sheets settings cleared successfully");
    } catch (error) {
      console.error("Error clearing  sheets settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const extractIdsFromSheetsUrl = (url) => {
    try {
      // Pattern: https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit?gid=SHEET_ID#gid=SHEET_ID
      const spreadsheetIdMatch = url.match(
        /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/,
      );
      const sheetIdMatch = url.match(/[?#]gid=([0-9]+)/);

      if (spreadsheetIdMatch && sheetIdMatch) {
        return {
          spreadsheetId: spreadsheetIdMatch[1],
          sheetId: sheetIdMatch[1],
          success: true,
          error: null,
        };
      } else if (spreadsheetIdMatch && !sheetIdMatch) {
        return {
          spreadsheetId: spreadsheetIdMatch[1],
          sheetId: "0", // Default to first sheet
          success: true,
          error: null,
        };
      } else {
        return {
          spreadsheetId: "",
          sheetId: "",
          success: false,
          error: "Could not extract IDs from URL. Please check the format.",
        };
      }
    } catch (error) {
      return {
        spreadsheetId: "",
        sheetId: "",
        success: false,
        error: "Invalid URL format.",
      };
    }
  };

  const validateColumnInput = (value) => {
    // Allow single letters A-Z or empty
    return value === "" || /^[A-Z]$/.test(value);
  };

  const getErrorMessage = (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Sheet Settings
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure Google Sheets integration for the functionality. Set up your
        spreadsheet connection and column mappings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/*  Google Sheets Configuration */}
      <Card sx={{ backgroundColor: "#34A853", color: "white", mb: 3 }}>
        <CardContent>
          <MDTypography
            variant="h6"
            fontWeight="medium"
            mb={2}
            sx={{ color: "white" }}
          >
            Google Sheets Configuration
          </MDTypography>

          {/* URL Input Section */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          >
            <MDTypography
              variant="body1"
              fontWeight="medium"
              mb={1}
              sx={{ color: "white" }}
            >
              Option 1: Paste Google Sheets URL
            </MDTypography>

            <TextField
              fullWidth
              label=" Google Sheets URL"
              placeholder="https://docs.google.com/spreadsheets/d/..."
              value={SheetsUrl}
              onChange={(e) => handleSheetsUrlChange(e.target.value)}
              error={!!SheetsUrlError}
              helperText={SheetsUrlError || "URL will be automatically parsed"}
              sx={{
                mb: 1,
                "& .MuiInputBase-input": { color: "white" },
                "& .MuiInputLabel-root": {
                  color: "rgba(255,255,255,0.7)",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                },
              }}
            />
          </Paper>

          {/* Manual Input Section */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          >
            <MDTypography
              variant="body1"
              fontWeight="medium"
              mb={1}
              sx={{ color: "white" }}
            >
              Option 2: Manual Input
            </MDTypography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label=" Spreadsheet ID"
                  placeholder="1TSodLjztKpEgCWMZdfaktzJSdVXKUUgzHMIcPfsBLQM"
                  value={SheetSettings.spreadsheetId}
                  onChange={(e) =>
                    handleSheetSettingsChange("spreadsheetId", e.target.value)
                  }
                  size="small"
                  sx={{
                    mb: 1,
                    "& .MuiInputBase-input": { color: "white" },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Sheet ID"
                  placeholder="304143252"
                  value={SheetSettings.sheetId}
                  onChange={(e) =>
                    handleSheetSettingsChange("sheetId", e.target.value)
                  }
                  size="small"
                  sx={{
                    "& .MuiInputBase-input": { color: "white" },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>
                    API Version
                  </InputLabel>
                  <Select
                    value={SheetSettings.version}
                    label="API Version"
                    onChange={(e) =>
                      handleSheetSettingsChange("version", e.target.value)
                    }
                    sx={{
                      color: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                      "& .MuiSvgIcon-root": { color: "white" },
                    }}
                  >
                    <MenuItem value="v4">v4</MenuItem>
                    <MenuItem value="v3">v3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Current Settings Display */}
          <Box
            sx={{
              pt: 1,
              borderTop: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <MDTypography
              variant="body2"
              fontWeight="medium"
              mb={1}
              sx={{ color: "white" }}
            >
              Current Settings:
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontSize: "0.75rem",
              }}
            >
              {SheetSettings.spreadsheetId ? (
                <a
                  href={`https://docs.google.com/spreadsheets/d/${
                    SheetSettings.spreadsheetId
                  }/edit${
                    SheetSettings.sheetId ? `?gid=${SheetSettings.sheetId}` : ""
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "inherit",
                    textDecoration: "underline",
                  }}
                >
                  {`https://docs.google.com/spreadsheets/d/${
                    SheetSettings.spreadsheetId
                  }/edit${
                    SheetSettings.sheetId ? `?gid=${SheetSettings.sheetId}` : ""
                  }`}
                </a>
              ) : (
                "Not set"
              )}{" "}
              {" | "}
              Version: {SheetSettings.version}
            </MDTypography>
          </Box>

          {/*  Column Settings */}
          <Box
            sx={{
              pt: 2,
              mt: 2,
              borderTop: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <MDTypography
              variant="h6"
              fontWeight="medium"
              mb={2}
              sx={{ color: "white" }}
            >
              Column Settings
            </MDTypography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date Column"
                  placeholder="A"
                  value={ColumnSettings.dateColumn}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    if (validateColumnInput(value)) {
                      handleColumnSettingsChange("dateColumn", value);
                    }
                  }}
                  helperText="e.g., A"
                  size="small"
                  inputProps={{
                    style: {
                      textTransform: "uppercase",
                      color: "white",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Order Place Column"
                  placeholder="B"
                  value={ColumnSettings.orderPlaceColumn}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    if (validateColumnInput(value)) {
                      handleColumnSettingsChange("orderPlaceColumn", value);
                    }
                  }}
                  helperText="e.g., B"
                  size="small"
                  inputProps={{
                    style: {
                      textTransform: "uppercase",
                      color: "white",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Unit Column"
                  placeholder="C"
                  value={ColumnSettings.unitColumn}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    if (validateColumnInput(value)) {
                      handleColumnSettingsChange("unitColumn", value);
                    }
                  }}
                  helperText="e.g., C"
                  size="small"
                  inputProps={{
                    style: {
                      textTransform: "uppercase",
                      color: "white",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ASIN/SKU Column"
                  placeholder="D"
                  value={ColumnSettings.asinSkuColumn}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    if (validateColumnInput(value)) {
                      handleColumnSettingsChange("asinSkuColumn", value);
                    }
                  }}
                  helperText="e.g., D"
                  size="small"
                  inputProps={{
                    style: {
                      textTransform: "uppercase",
                      color: "white",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Send Status Column"
                  placeholder="E"
                  value={ColumnSettings.sendStatusColumn}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    if (validateColumnInput(value)) {
                      handleColumnSettingsChange("sendStatusColumn", value);
                    }
                  }}
                  helperText="e.g., E"
                  size="small"
                  inputProps={{
                    style: {
                      textTransform: "uppercase",
                      color: "white",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Confirmation Column"
                  placeholder="F"
                  value={ColumnSettings.confirmationColumn}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    if (validateColumnInput(value)) {
                      handleColumnSettingsChange("confirmationColumn", value);
                    }
                  }}
                  helperText="e.g., F"
                  size="small"
                  inputProps={{
                    style: {
                      textTransform: "uppercase",
                      color: "white",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={saveSheetsSettings}
          disabled={loading}
          sx={{
            backgroundColor: "#34A853",
            "&:hover": {
              backgroundColor: "#2E8B47",
            },
          }}
        >
          {loading ? "Saving..." : "Save Settings"}
        </Button>

        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={clearSheetsSettings}
          disabled={loading}
          sx={{
            color: "#EA4335",
            borderColor: "#EA4335",
            "&:hover": {
              backgroundColor: "rgba(234, 67, 53, 0.1)",
              borderColor: "#EA4335",
            },
          }}
        >
          Clear Settings
        </Button>
      </Box>

      {/* Info Section */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>How to use:</strong>
          <br />
          1. Create a Google Sheet with your data
          <br />
          2. Share the sheet with your service account email
          <br />
          3. Copy the sheet URL and paste it above, or enter IDs manually
          <br />
          4. Configure column mappings to match your sheet structure
          <br />
          5. Save settings to enable functionality
        </Typography>
      </Alert>
    </Box>
  );
};

export default SheetSettingsSubTab;
